import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../services/api';

interface UserRecord {
  username: string;
  email: string;
  sub: string;
  zipcode: string;
  tier: string;
  paidPlus?: boolean;
  paidPlayPlus?: boolean;
  groups: string[];
  isAdmin: boolean;
  status: string;
  createdAt: string;
  lastActive?: string;
  enabled: boolean;
}

interface InactiveUser {
  username: string;
  email: string;
  lastActive: string;
  createdAt: string;
}

function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [inactiveDays, setInactiveDays] = useState(365);
  const [inactiveUsers, setInactiveUsers] = useState<InactiveUser[] | null>(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [userActionBusy, setUserActionBusy] = useState(false);
  const selectedUsername = searchParams.get('user');
  const selectedUser = users.find((user) => user.username === selectedUsername);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handlePreviewInactive = async () => {
    setActionMessage('');
    setCleanupBusy(true);
    try {
      const response = await adminApi.previewInactive(inactiveDays);
      setInactiveUsers(response.data.users);
    } catch (err: any) {
      setInactiveUsers(null);
      setActionMessage(`Error: ${err.response?.data?.error || 'Failed to find inactive users'}`);
    } finally {
      setCleanupBusy(false);
    }
  };

  const handlePurgeInactive = async () => {
    if (!inactiveUsers || inactiveUsers.length === 0) return;
    if (!window.confirm(`Permanently delete ${inactiveUsers.length} free user(s) inactive for over ${inactiveDays} days? This cannot be undone.`)) {
      return;
    }
    setActionMessage('');
    setCleanupBusy(true);
    try {
      const response = await adminApi.purgeInactive(inactiveDays);
      const { deleted, failed } = response.data;
      setActionMessage(`Deleted ${deleted} inactive user${deleted !== 1 ? 's' : ''}${failed ? `, ${failed} failed` : ''}`);
      setInactiveUsers(null);
      fetchUsers();
    } catch (err: any) {
      setActionMessage(`Error: ${err.response?.data?.error || 'Failed to delete inactive users'}`);
    } finally {
      setCleanupBusy(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.listUsers();
      setUsers(response.data.users);
      setBillingEnabled(response.data.billingEnabled || false);
    } catch (err: any) {
      setError('Failed to load users.');
      console.error('Admin fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTierChange = async (username: string, newTier: string) => {
    setActionMessage('');
    setUserActionBusy(true);
    try {
      await adminApi.changeUserTier(username, newTier);
      setActionMessage(billingEnabled
        ? `Complimentary Plus ${newTier === 'plus' ? 'granted' : 'removed'} for ${username}. Paid access is unchanged.`
        : `Tier updated to "${newTier}" for ${username}`);
      await fetchUsers();
    } catch (err: any) {
      setActionMessage(`Error: ${err.response?.data?.error || 'Failed to update tier'}`);
    } finally {
      setUserActionBusy(false);
    }
  };

  const handleDelete = async (username: string, email: string) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This cannot be undone.`)) {
      return;
    }
    setActionMessage('');
    setUserActionBusy(true);
    try {
      await adminApi.deleteUser(username);
      setActionMessage(`User ${email} deleted`);
      setSearchParams({});
      await fetchUsers();
    } catch (err: any) {
      setActionMessage(`Error: ${err.response?.data?.error || 'Failed to delete user'}`);
    } finally {
      setUserActionBusy(false);
    }
  };

  const getTierBadgeClass = (tier: string): string => {
    switch (tier) {
      case 'plus': return 'tier-badge tier-plus';
      default: return 'tier-badge tier-free';
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading" role="status">
          <div className="loading-spinner" aria-hidden="true"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchUsers} className="btn btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2>{selectedUsername ? 'User Details' : 'User Management'}</h2>
        {!selectedUsername && (
          <p className="admin-subtitle">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>
        )}
      </div>

      {actionMessage && (
        <div role="status" className={`alert ${actionMessage.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {actionMessage}
        </div>
      )}

      {selectedUsername ? (
        <>
          <button className="btn btn-secondary btn-sm admin-back-button" onClick={() => setSearchParams({})} disabled={userActionBusy}>
            Back to users
          </button>
          {selectedUser ? (
            <section className="admin-user-details" aria-label="User details">
              <h3>{selectedUser.email}</h3>
              <dl className="admin-detail-list">
                <div><dt>Tier</dt><dd><span className={getTierBadgeClass(selectedUser.tier)}>{selectedUser.tier}</span></dd></div>
                <div><dt>Administrator</dt><dd>{selectedUser.isAdmin ? 'Yes' : 'No'}</dd></div>
                <div><dt>Zipcode</dt><dd>{selectedUser.zipcode || '—'}</dd></div>
                <div><dt>Created</dt><dd>{selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : '—'}</dd></div>
                <div><dt>Last active</dt><dd>{selectedUser.lastActive ? new Date(selectedUser.lastActive).toLocaleDateString() : '—'}</dd></div>
                <div><dt>Account status</dt><dd>{selectedUser.status}</dd></div>
                <div><dt>Account enabled</dt><dd>{selectedUser.enabled ? 'Yes' : 'No'}</dd></div>
              </dl>
              <div className="admin-user-actions">
                <label htmlFor="admin-user-tier">{billingEnabled ? 'Complimentary access' : 'Tier'}</label>
                <select
                  id="admin-user-tier"
                  value={billingEnabled ? (selectedUser.groups.includes('plus') ? 'plus' : 'free') : selectedUser.tier}
                  onChange={(e) => handleTierChange(selectedUser.username, e.target.value)}
                  className="tier-select"
                  disabled={userActionBusy}
                >
                  <option value="free">{billingEnabled ? 'No complimentary Plus' : 'Free'}</option>
                  <option value="plus">{billingEnabled ? 'Complimentary Plus' : 'Plus'}</option>
                </select>
                {selectedUser.paidPlus && <p className="form-hint">Paid Plus subscription</p>}
                {billingEnabled && <p className="form-hint">Changing complimentary access does not change paid subscription access.</p>}
                <button
                  onClick={() => handleDelete(selectedUser.username, selectedUser.email)}
                  className="btn btn-danger btn-sm"
                  disabled={userActionBusy}
                >
                  Delete account
                </button>
              </div>
            </section>
          ) : (
            <div className="error-state" role="alert"><p>User not found. Return to the user list to select an account.</p></div>
          )}
        </>
      ) : (
        <>
          <div className="admin-table-wrapper">
            <table className="admin-table admin-user-table">
              <thead><tr><th scope="col">User</th><th scope="col">Tier</th></tr></thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.username}>
                    <td className="user-email-cell">
                      <button
                        className="admin-user-link"
                        onClick={() => { setActionMessage(''); setSearchParams({ user: user.username }); }}
                        aria-label={`View details for ${user.email}`}
                      >
                        {user.email}
                      </button>
                    </td>
                    <td>
                      <div className="admin-tier-badges">
                        <span className={getTierBadgeClass(user.tier)}>{user.tier}</span>
                        {user.paidPlayPlus ? (
                          <span className="admin-access-icon" role="img" aria-label="Google Play subscription" title="Google Play subscription">
                            <svg viewBox="80 70 104 110" aria-hidden="true" focusable="false">
                              <image href="/google-play-badge.png" width="646" height="250" />
                            </svg>
                          </span>
                        ) : user.paidPlus && (
                          <span className="admin-access-icon admin-access-symbol" role="img" aria-label="Paid subscription" title="Paid subscription">$</span>
                        )}
                        {user.groups.includes('plus') && (
                          <span className="admin-access-icon admin-access-symbol" role="img" aria-label="Manual Plus" title="Manual Plus">+</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && <p className="admin-empty-state">No registered users found.</p>}
          </div>
          <details className="admin-cleanup">
            <summary>Remove inactive free users</summary>
            <p className="admin-subtitle">
              Deletes Free accounts with no sign-in or session activity for the chosen period. Plus users and admins are never removed, and accounts with no recorded activity yet are skipped.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={inactiveDays}
                onChange={(e) => { setInactiveDays(parseInt(e.target.value, 10)); setInactiveUsers(null); }}
                className="tier-select"
                aria-label="Inactivity period"
                disabled={cleanupBusy}
              >
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>1 year</option>
                <option value={730}>2 years</option>
              </select>
              <button onClick={handlePreviewInactive} className="btn btn-primary btn-sm" disabled={cleanupBusy}>
                Preview
              </button>
              {inactiveUsers && inactiveUsers.length > 0 && (
                <button onClick={handlePurgeInactive} className="btn btn-danger btn-sm" disabled={cleanupBusy}>
                  Delete {inactiveUsers.length} user{inactiveUsers.length !== 1 ? 's' : ''}
                </button>
              )}
            </div>
            {inactiveUsers && (
              inactiveUsers.length === 0 ? (
                <p className="admin-subtitle">No inactive free users found.</p>
              ) : (
                <ul style={{ marginTop: '0.75rem' }}>
                  {inactiveUsers.map((u) => (
                    <li key={u.username}>{u.email} — last active {new Date(u.lastActive).toLocaleDateString()}</li>
                  ))}
                </ul>
              )
            )}
          </details>
        </>
      )}
    </div>
  );
}

export default AdminPage;
